// ── Domain types ─────────────────────────────────────────────────────────────

export type CheckState = 'open' | 'done' | 'in-progress';

/** Image queued for upload on next save (filename is pre-generated). */
export interface PendingItemImage {
  readonly file: File;
  readonly filename: string;
}

/** Audio clip queued for upload on next save (filename is pre-generated). */
export interface PendingItemAudio {
  readonly file: File;
  readonly filename: string;
}

/** Video clip queued for upload on next save (filename is pre-generated). */
export interface PendingItemVideo {
  readonly file: File;
  filename: string;
  /** JPEG preview extracted on attach (not persisted until save). */
  thumbPreviewUrl?: string;
  thumbBlob?: Blob;
}

/** Empty media attachment fields for a new checklist item. */
export function emptyCheckItemMedia(): Pick<
  CheckItem,
  | 'images'
  | 'pendingImages'
  | 'imagesToDelete'
  | 'audio'
  | 'pendingAudio'
  | 'audioToDelete'
  | 'video'
  | 'pendingVideo'
  | 'videoToDelete'
  | 'videoRenames'
> {
  return {
    images: [],
    pendingImages: [],
    imagesToDelete: [],
    audio: [],
    pendingAudio: [],
    audioToDelete: [],
    video: [],
    pendingVideo: [],
    videoToDelete: [],
    videoRenames: [],
  };
}

export interface CheckItem {
  raw: string;        // original markdown line
  text: string;       // display text (without - [ ] prefix)
  date: string;       // YYYY-MM-DD
  state: CheckState;
  /** Filenames under `{page}/_images/` (see itemImages.ts). */
  images: string[];
  /** Selected locally; uploaded when the page is saved. */
  pendingImages: PendingItemImage[];
  /** Repo filenames to remove on save. */
  imagesToDelete: string[];
  /** Filenames under `{page}/_audio/` (see itemAudio.ts). */
  audio: string[];
  pendingAudio: PendingItemAudio[];
  audioToDelete: string[];
  /** Filenames under `{page}/_video/` (see itemVideo.ts). */
  video: string[];
  pendingVideo: PendingItemVideo[];
  videoToDelete: string[];
  /** Committed clips renamed locally; applied on save (mp4 + .thumb.jpg). */
  videoRenames: { from: string; to: string }[];
}

/** How checklist rows are ordered in the UI (storage stays in array order). */
export type SectionItemSort = 'newest' | 'oldest';

export interface Section {
  title: string;
  readonly level: number;      // heading level (## = 2, ### = 3)
  items: CheckItem[];
  prose: string[];             // non-checklist lines
  /** Display order; default newest (top = index 0). Persisted via markdown HTML comment. */
  itemSort?: SectionItemSort;
}

export interface DatabaseFile {
  path: string;       // e.g. "database/notes.md"
  name: string;       // display name e.g. "Notes & To-Dos"
  sections: Section[];
  sha: string;        // GitHub blob SHA (needed for updates)
  /** Full repo paths of images to delete on save (e.g. after item removed). */
  orphanedImagePaths: string[];
  /** Full repo paths of audio clips to delete on save (e.g. after item removed). */
  orphanedAudioPaths: string[];
  orphanedVideoPaths: string[];
}

export interface GitHubConfig {
  readonly owner: string;
  readonly repo: string;
  readonly branch: string;
  readonly pat: string;
  /** When true or omitted, public CDNs (jsDelivr, github/raw) are not used for media. */
  readonly private?: boolean;
}

/** Sidebar sibling order under a folder (parent key is e.g. database or database/notes). */
export type NavOrderEntry =
  | { kind: 'file'; path: string }
  | { kind: 'dir'; dir: string };

export interface NavOrderFile {
  version: 2;
  order: Record<string, NavOrderEntry[]>;
  /** Custom sidebar emoji per markdown path (e.g. database/notes/todos.md). */
  icons?: Record<string, string>;
}
